// ===============================================================
// PUSH BİLDİRİM SERVİSİ (Capacitor Yerel Android & Web Entegrasyonu)
// ===============================================================

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

const PUSHED_NOTIFICATIONS_KEY = 'kocum_device_pushed_ids';

/**
 * Cihazın yerel mobil platformda (Android / iOS) çalışıp çalışmadığını doğrular
 */
export const isNativePlatform = () => {
  return typeof window !== 'undefined' && Capacitor.isNativePlatform();
};

/**
 * Service Worker'ı kaydeder (Web platformu için)
 */
export async function registerServiceWorker() {
  if (typeof window === 'undefined') return null;

  // Yerel mobilde ise yerel bildirim izinlerini kontrol et / hazırla
  if (isNativePlatform()) {
    try {
      await LocalNotifications.requestPermissions();
    } catch (e) {
      console.warn('Capacitor LocalNotifications izin kontrolü:', e);
    }
    return null;
  }

  // Web ortamı için Service Worker kaydı
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      return registration;
    } catch (error) {
      console.warn('Service Worker kaydı yapılamadı:', error);
      return null;
    }
  }
  return null;
}

/**
 * Tarayıcı / Cihaz bildirim izni durumunu kontrol eder
 */
export async function getNotificationPermissionStatus() {
  if (typeof window === 'undefined') return 'unsupported';

  // 1. Android / iOS Yerel Mobil Uygulama
  if (isNativePlatform()) {
    try {
      const status = await LocalNotifications.checkPermissions();
      return status.display === 'granted' ? 'granted' : status.display === 'denied' ? 'denied' : 'default';
    } catch (e) {
      return 'default';
    }
  }

  // 2. Web Tarayıcı
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

/**
 * Kullanıcıdan bildirim izni ister
 */
export async function requestNotificationPermission() {
  if (typeof window === 'undefined') return 'denied';

  // 1. Android / iOS Yerel Mobil Uygulama (Android 13+ POST_NOTIFICATIONS)
  if (isNativePlatform()) {
    try {
      const res = await LocalNotifications.requestPermissions();
      return res.display === 'granted' ? 'granted' : 'denied';
    } catch (e) {
      console.error('Yerel bildirim izni istenirken hata:', e);
      return 'denied';
    }
  }

  // 2. Web Tarayıcı
  if (!('Notification' in window)) {
    return 'unsupported';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Bildirim izni istenirken hata oluştu:', error);
    return 'denied';
  }
}

/**
 * Cihazın durum çubuğundan üstten düşen anlık sistem bildirimi gösterir
 */
export async function showSystemPushNotification({
  title = 'Koçum Online',
  message = '',
  type = 'info',
  icon = '/favicon.ico',
  url = '/'
}) {
  if (typeof window === 'undefined') return false;

  // Bildirim ön eki
  let prefix = '📢';
  if (type === 'urgent') prefix = '🚨';
  if (type === 'warning') prefix = '⚠️';
  if (type === 'success') prefix = '🎉';

  const fullTitle = `${prefix} ${title}`;
  const cleanBody = message || 'Yeni bir duyuru veya bildiriminiz var.';

  // 1. ANDROID / IOS YEREL MOBİL BİLDİRİM (CAPACITOR NATIVE)
  if (isNativePlatform()) {
    try {
      const perm = await LocalNotifications.checkPermissions();
      if (perm.display !== 'granted') {
        const req = await LocalNotifications.requestPermissions();
        if (req.display !== 'granted') return false;
      }

      const notifId = Math.floor(Math.random() * 2147483647);
      await LocalNotifications.schedule({
        notifications: [
          {
            id: notifId,
            title: fullTitle,
            body: cleanBody,
            smallIcon: 'ic_launcher_round',
            sound: null,
            vibrate: true,
            extra: { url: url || '/' }
          }
        ]
      });
      return true;
    } catch (nativeErr) {
      console.error('Yerel bildirim fırlatma hatası:', nativeErr);
      return false;
    }
  }

  // 2. WEB TARAYICI BİLDİRİMİ (NOTIFICATION API)
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission !== 'granted') {
    const perm = await requestNotificationPermission();
    if (perm !== 'granted') return false;
  }

  const options = {
    body: cleanBody,
    icon: icon,
    badge: icon,
    tag: `kocum-notif-${Date.now()}`,
    renotify: true,
    requireInteraction: true,
    vibrate: [200, 100, 200, 100, 200],
    data: { url: url || '/' }
  };

  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.ready;
      if (reg && reg.showNotification) {
        await reg.showNotification(fullTitle, options);
        return true;
      }
    }

    new Notification(fullTitle, options);
    return true;
  } catch (err) {
    try {
      new Notification(fullTitle, options);
      return true;
    } catch (e) {
      return false;
    }
  }
}

/**
 * Kullanıcının daha önce bu push bildirimini alıp almadığını kontrol eder
 */
export function hasBeenPushedLocally(id) {
  try {
    const raw = localStorage.getItem(PUSHED_NOTIFICATIONS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return list.includes(id);
  } catch (e) {
    return false;
  }
}

/**
 * Bildirimin cihaza üstten düşürüldüğünü yerel hafızaya kaydeder
 */
export function markAsPushedLocally(id) {
  try {
    const raw = localStorage.getItem(PUSHED_NOTIFICATIONS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!list.includes(id)) {
      list.push(id);
      localStorage.setItem(PUSHED_NOTIFICATIONS_KEY, JSON.stringify(list.slice(-100)));
    }
  } catch (e) {}
}

/**
 * Yeni gelen bir bildirim için push tetiği çalıştırır
 */
export function handleIncomingPushItem(item, currentUser) {
  if (!item || !currentUser || !currentUser.id) return;

  // Sadece 'push' veya 'both' kanallı bildirimler üstten düşer
  const channel = item.deliveryChannel || 'both';
  if (channel !== 'push' && channel !== 'both') return;

  // Aktif değilse gönderme
  if (!item.isActive) return;

  // Daha önce sesli/üstten düşmüşse tekrar bildirme
  if (hasBeenPushedLocally(item.id)) return;

  // Hedef kitle kontrolü
  let matches = false;
  const uidStr = String(currentUser.id).trim();
  const targetType = item.targetType || item.target || 'all';

  if (targetType === 'all') {
    matches = true;
  } else if (targetType === 'students') {
    matches = currentUser.role === 'student' || !currentUser.role;
  } else if (targetType === 'teachers') {
    matches = currentUser.role === 'admin' || currentUser.role === 'superadmin';
  } else if (targetType === 'custom') {
    if (Array.isArray(item.targetUserIds)) {
      matches = item.targetUserIds.some(id => String(id).trim() === uidStr);
    }
  }

  if (matches) {
    markAsPushedLocally(item.id);
    showSystemPushNotification({
      title: item.title,
      message: item.message,
      type: item.type,
      url: item.actionLink || '/'
    });
  }
}
